import { useEffect, useMemo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import { getEligibleEsimOrders, filterEsimEligibleOrdersForView } from 'loyalty/lib/getEligibleEsimOrders'
import fetchEsimPromoCodesAction from 'actions/loyalty/fetchEsimPromoCodes'
import { fetchOrder, fetchOrders } from 'actions/OrderActions'
import { UPDATE_LOYALTY_ESIM_CODE } from 'actions/actionConstants'
import { INITIAL_ESIM_PROMO_CODES_STATE } from 'reducers/loyaltyStateReducer'
import { useIntl } from 'react-intl'
import pollOrderService from 'lib/polling/pollOrderService'
import { getOrderById } from 'api/orderLookup'
import { getUpcomingAndCurrentOrders } from 'selectors/orderSelectors'

function getAllOrderItems(order: App.Order) {
  // Hotel, bedbank, flight, cruise, tour — aligned with date/country collectors in `getEligibleEsimOrders`.
  return [...order.items, ...order.bedbankItems, ...order.flightItems, ...(order.cruiseItems ?? []), ...(order.tourItems ?? [])]
}

const PENDING_ORDER_POLL_WAIT_MS = 30_000
const PENDING_ORDER_POLL_MAX_MS = 2 * 60 * 1_000

export type EsimVariation = 'none' | 'single' | 'multiple'

export interface UseEsimPromoCodesResult {
  variation: EsimVariation
  codes: Array<App.LoyaltyEsimPromoCodeEntry>
  singleCode: App.LoyaltyEsimPromoCodeEntry | undefined
  singleOrderPhase: App.LoyaltyEsimCodePhase | undefined
  isLoading: boolean
  isEligibleListReady: boolean
}

interface UseEsimPromoCodesOptions {
  orderId?: string
}

// Eligibility / 7-day: `getEligibleEsimOrders`. Redeem-before-confirm → `booking_pending` + poll until
// eSIM line items are completed status → `fetchOrder` → `fetchEsimPromoCodes`.
function useEsimPromoCodes(options?: UseEsimPromoCodesOptions): UseEsimPromoCodesResult {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const orderIdFilter = options?.orderId
  const ordersFetchedAll = useAppSelector(state => state.orders.ordersFetched.all)
  const upcomingAndCurrentOrders = useAppSelector(getUpcomingAndCurrentOrders)
  const accountRegion = useAppSelector(getLoyaltyAccountRegion)

  // Full order list (`fetchOrders({})`, not `status: 'upcoming'`) so in-trip stays are in Redux for eSIM.
  useEffect(() => {
    if (!ordersFetchedAll) dispatch(fetchOrders({}))
  }, [dispatch, ordersFetchedAll])
  const flightDetails = useAppSelector(state => state.orders.flightDetails)
  const rawEsimState = useAppSelector(state => state.loyalty.esimPromoCodes)
  const esimState = rawEsimState?.codesByOrderId ? rawEsimState : INITIAL_ESIM_PROMO_CODES_STATE
  const esimExcludedCountryCodes = useAppSelector(state => state.loyalty.config?.esimExcludedCountryCodes)
  const internationalTripLabel = intl.formatMessage({
    id: 'w2csFNE5b6Q3-38YywXP',
    defaultMessage: 'International trip',
    description: 'Fallback destination label for eligible eSIM orders when specific destination names are unavailable',
  })

  /** `getUpcomingAndCurrentOrders` always returns an array (memoized selector). */
  const orderListForEsim = upcomingAndCurrentOrders

  const hasOrdersWithCruise = useMemo(() => {
    const ordersToCheck = orderIdFilter
      ? orderListForEsim.filter(o => o.id === orderIdFilter)
      : orderListForEsim
    return ordersToCheck.some(
      o => o.status !== 'cancelled' && o.cruiseItems?.some(item => item.status === 'completed'),
    )
  }, [orderListForEsim, orderIdFilter])

  const eligibleOrdersUnfiltered = useMemo(() => {
    if (!accountRegion || !orderListForEsim.length) return []
    return getEligibleEsimOrders(
      orderListForEsim,
      accountRegion,
      intl.locale,
      internationalTripLabel,
      flightDetails,
      esimState.cruiseCountryCodesByOrderId,
      esimExcludedCountryCodes,
    )
  }, [orderListForEsim, accountRegion, flightDetails, esimState.cruiseCountryCodesByOrderId, esimExcludedCountryCodes, intl.locale, internationalTripLabel])

  const eligibleOrders = useMemo(
    () => filterEsimEligibleOrdersForView(eligibleOrdersUnfiltered, orderIdFilter),
    [eligibleOrdersUnfiltered, orderIdFilter],
  )

  const eligibleOrderIdsKey = useMemo(() => eligibleOrders.map(o => o.orderId).join(','), [eligibleOrders])

  const isTargetOrderEligible = useMemo(
    () => !orderIdFilter || eligibleOrdersUnfiltered.some(o => o.orderId === orderIdFilter),
    [orderIdFilter, eligibleOrdersUnfiltered],
  )

  // True when the target order exists but isn't eligible yet because its line items are still pending
  // (e.g. user opens the eSIM modal immediately after checkout before the booking is confirmed).
  // Once all items reach completed/cancelled the order re-enters eligibility checks and normal
  // eSIM code polling kicks in via fetchEsimPromoCodes.
  const isBookingPending = useMemo(() => {
    if (!orderIdFilter || isTargetOrderEligible) return false
    const targetOrder = orderListForEsim.find(o => o.id === orderIdFilter)
    if (!targetOrder) return false
    return getAllOrderItems(targetOrder).some(item => item.status !== 'completed' && item.status !== 'cancelled')
  }, [orderIdFilter, isTargetOrderEligible, orderListForEsim])

  // Keyed by orderId (not a plain boolean) so navigating from order A → B while A's poll is
  // still in flight doesn't prevent B from starting its own poll.
  const pollingOrderIdRef = useRef<string | null>(null)

  const esimPhaseForOrder = orderIdFilter ? esimState.codesByOrderId[orderIdFilter]?.phase : undefined

  // Clears a stale booking_pending phase when the booking has settled (items completed/cancelled)
  // but the order still isn't eligible (e.g. domestic trip). Handles both the live transition and
  // the reopen-after-confirm case where the order confirmed while the modal was closed.
  useEffect(() => {
    if (!orderIdFilter || isBookingPending || isTargetOrderEligible) return
    if (esimPhaseForOrder === 'booking_pending') {
      dispatch({ type: UPDATE_LOYALTY_ESIM_CODE, orderId: orderIdFilter, update: { phase: 'not_found' } })
    }
  }, [dispatch, orderIdFilter, isBookingPending, isTargetOrderEligible, esimPhaseForOrder])

  // Polls the order API until all line items are completed/cancelled, then force-refreshes the
  // order in Redux to trigger fetchEsimPromoCodes with the eligible order.
  // capturedOrderId is used instead of the closure's orderIdFilter so that if the user
  // navigates to a different order, this poll's callbacks still refer to the correct order.
  useEffect(() => {
    if (!isBookingPending || !orderIdFilter || pollingOrderIdRef.current === orderIdFilter) return
    dispatch({ type: UPDATE_LOYALTY_ESIM_CODE, orderId: orderIdFilter, update: { orderId: orderIdFilter, destination: '', dates: '', phase: 'booking_pending' } })

    pollingOrderIdRef.current = orderIdFilter
    const capturedOrderId = orderIdFilter
    let cancelled = false
    pollOrderService({
      validateFunction: (order: App.Order) =>
        getAllOrderItems(order).every(item => item.status === 'completed' || item.status === 'cancelled') ? order : undefined,
      failedStatus: 'failed',
      apiCall: () => getOrderById(orderIdFilter),
      maxTime: PENDING_ORDER_POLL_MAX_MS,
      waitTime: PENDING_ORDER_POLL_WAIT_MS,
    }).then(() => {
      // Only clear the ref if this poll is still the active one (guards against A's poll clearing B's ref)
      if (pollingOrderIdRef.current === capturedOrderId) pollingOrderIdRef.current = null
      if (cancelled) return
      dispatch(fetchOrder(capturedOrderId, { force: true }))
    }).catch(() => {
      if (pollingOrderIdRef.current === capturedOrderId) pollingOrderIdRef.current = null
      if (cancelled) return
      dispatch({ type: UPDATE_LOYALTY_ESIM_CODE, orderId: capturedOrderId, update: { phase: 'error' } })
    })

    return () => {
      cancelled = true
      // Clear the ref on unmount so reopening the same order starts a fresh poll immediately
      if (pollingOrderIdRef.current === capturedOrderId) pollingOrderIdRef.current = null
    }
  }, [dispatch, orderIdFilter, isBookingPending])

  useEffect(() => {
    dispatch(fetchEsimPromoCodesAction(orderIdFilter))
  }, [dispatch, orderIdFilter, eligibleOrderIdsKey])

  const variation = useMemo<EsimVariation>(() => {
    if (eligibleOrders.length === 0) return 'none'
    if (eligibleOrders.length === 1) return 'single'
    return 'multiple'
  }, [eligibleOrders.length])

  const codes = useMemo(() => {
    const list = eligibleOrders
      .map(order => esimState.codesByOrderId[order.orderId])
      .filter((entry): entry is App.LoyaltyEsimPromoCodeEntry => !!entry && entry.phase !== 'not_found')
    // Show loading/generating first so the taller card doesn't create a hole in the grid; once done, order is by departure date
    const loadingOrGenerating = list.filter(c => c.phase === 'loading' || c.phase === 'generating')
    const rest = list.filter(c => c.phase !== 'loading' && c.phase !== 'generating')
    return [...loadingOrGenerating, ...rest]
  }, [eligibleOrders, esimState.codesByOrderId])

  const singleCode = variation === 'single' ? codes[0] : undefined
  const singleOrderPhase = useMemo(() => {
    if (orderIdFilter) return esimState.codesByOrderId[orderIdFilter]?.phase
    if (variation === 'single' && eligibleOrders.length === 1) return esimState.codesByOrderId[eligibleOrders[0].orderId]?.phase
    return undefined
  }, [orderIdFilter, variation, eligibleOrders, esimState.codesByOrderId])
  const isLoading = codes.some(code => code.phase === 'loading' || code.phase === 'generating')
  const isEligibleListReady = !hasOrdersWithCruise || esimState.cruiseCodesSettled

  return { variation, codes, singleCode, singleOrderPhase, isLoading, isEligibleListReady }
}

export default useEsimPromoCodes
